import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Honeypot from "../components/Honeypot";
import VehicleStatus from "../components/VehicleStatus";
import { apiError } from "../lib/errors";
import JsonLd from "../components/JsonLd";
import { useAuth } from "../context/AuthContext";

const fmt = (n) => `$${Number(n).toLocaleString()}`;
const DEFAULT_SITE = "https://mbgautosales.com";
const inputCls = "bg-[#E2D4C1] border-[#E2D4C1] rounded-none focus-visible:ring-gold";
const safeUrl = (u) => (/^https?:\/\//i.test(u || "") ? u : null); // only link out to http(s) documents

export default function CarDetail() {
    const { id } = useParams();
    const [v, setV] = useState(null);
    const [state, setState] = useState("loading"); // loading | ready | notfound | error
    const [photo, setPhoto] = useState(0);
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", website: "" });
    const [sending, setSending] = useState(false);
    const [reserving, setReserving] = useState(false);
    const [deposit, setDeposit] = useState(500);
    const [site, setSite] = useState(DEFAULT_SITE);
    const { user } = useAuth();

    useEffect(() => {
        setState("loading"); setPhoto(0);
        api.get(`/vehicles/${id}`)
            .then(r => { setV(r.data); setState("ready"); })
            .catch(e => setState(e?.response?.status === 404 ? "notfound" : "error"));
    }, [id]);
    useEffect(() => {
        api.get("/config/public").then(r => {
            setDeposit(r.data.reserve_deposit_amount || 500);
            if (r.data.site_url) setSite(r.data.site_url.replace(/\/$/, ""));
        }).catch(() => {});
    }, []);

    const photos = useMemo(() => {
        if (!v) return [];
        return [v.image, ...(v.images || [])].filter((u, i, arr) => u && arr.indexOf(u) === i);
    }, [v]);

    const reserve = async () => {
        if (!user && !form.email) {
            toast.error("Enter your email to start a guest reservation.");
            return;
        }
        setReserving(true);
        try {
            const { data } = await api.post(`/vehicles/${id}/reserve`, {
                origin_url: window.location.origin,
                name: form.name || undefined,
                email: form.email || undefined,
                phone: form.phone || undefined,
            });
            window.location.href = data.url;
        } catch (err) {
            toast.error(apiError(err, "Could not start reservation. Please try again."));
            setReserving(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post("/inquiries", { type: "auto_sales", ...form, vehicle_id: id, vehicle_info: `${v.year} ${v.make} ${v.model}` });
            toast.success("Inquiry sent. Our sales team will reach out shortly.");
            setForm({ name: "", email: "", phone: "", message: "", website: "" });
        } catch (e) { toast.error(apiError(e, "Failed to send inquiry")); }
        setSending(false);
    };

    const vehicleSchema = useMemo(() => {
        if (!v) return null;
        const availability = v.status === "sold" ? "https://schema.org/SoldOut"
            : (v.status === "reserved" || v.status === "pending") ? "https://schema.org/LimitedAvailability"
            : "https://schema.org/InStock";
        return {
            "@context": "https://schema.org",
            "@type": "Car",
            name: `${v.year} ${v.make} ${v.model}`,
            brand: { "@type": "Brand", name: v.make },
            model: v.model,
            vehicleModelDate: String(v.year),
            ...(v.trim ? { vehicleConfiguration: v.trim } : {}),
            ...(v.mileage != null ? { mileageFromOdometer: { "@type": "QuantitativeValue", value: v.mileage, unitCode: "SMI" } } : {}),
            ...(v.body_type ? { bodyType: v.body_type } : {}),
            ...(v.transmission ? { vehicleTransmission: v.transmission } : {}),
            ...(v.fuel ? { fuelType: v.fuel } : {}),
            ...(v.color ? { color: v.color } : {}),
            ...(v.vin ? { vehicleIdentificationNumber: v.vin } : {}),
            ...(photos.length ? { image: photos } : {}),
            ...(v.description ? { description: v.description } : {}),
            url: `${site}/cars/${v.id}`,
            offers: {
                "@type": "Offer",
                price: v.price,
                priceCurrency: "USD",
                availability,
                itemCondition: "https://schema.org/UsedCondition",
                url: `${site}/cars/${v.id}`,
                seller: { "@type": "AutoDealer", name: "MBG Auto Sales" },
            },
        };
    }, [v, photos, site]);

    if (state === "loading") return <div role="status" className="pt-32 text-center text-[#6B645C]">Loading…</div>;
    if (state === "notfound" || state === "error") {
        return (
            <div data-testid={state === "notfound" ? "car-not-found" : "car-error"} className="max-w-xl mx-auto px-6 pt-32 pb-24 text-center">
                <h1 className="font-display text-4xl mb-4">{state === "notfound" ? "This vehicle is no longer listed." : "We couldn't load this vehicle."}</h1>
                <p className="text-[#6B645C] mb-8">{state === "notfound" ? "It may have been sold or removed. Browse the current showroom or tell us what you're looking for." : "Please try again in a moment."}</p>
                <div className="flex flex-wrap justify-center gap-3">
                    <Link to="/cars" className="px-6 py-3 bg-gold text-white uppercase tracking-[0.2em] text-xs font-semibold hover:bg-gold-hover">Browse Vehicles</Link>
                    <Link to="/contact?topic=vehicle" className="px-6 py-3 border border-[#8C5A3C]/40 uppercase tracking-[0.2em] text-xs hover:border-gold hover:text-gold">Contact Sales</Link>
                </div>
            </div>
        );
    }

    const docs = [
        ["Vehicle history report", safeUrl(v.carfax_url)],
        ["Window sticker", safeUrl(v.window_sticker_url)],
    ].filter(([, u]) => u);
    const specs = [
        ["Year", v.year], ["Make", v.make], ["Model", v.model], ...(v.trim ? [["Trim", v.trim]] : []),
        ["Mileage", `${Number(v.mileage).toLocaleString()} mi`],
        ["Transmission", v.transmission || "—"], ...(v.engine ? [["Engine", v.engine]] : []),
        ...(v.drivetrain ? [["Drivetrain", v.drivetrain]] : []), ["Fuel", v.fuel || "—"],
        ["Body", v.body_type || "—"], ["Exterior", v.exterior_color || v.color || "—"],
        ...(v.interior_color ? [["Interior", v.interior_color]] : []), ["VIN", v.vin || "—"],
    ];

    return (
        <div data-testid="car-detail-page" className="max-w-7xl mx-auto px-6 md:px-12 py-12">
            <JsonLd data={vehicleSchema} />
            <Link to="/cars" className="text-[11px] tracking-[0.3em] uppercase text-[#6B645C] hover:text-gold">← Back to Showroom</Link>

            <div className="grid lg:grid-cols-[1.5fr_1fr] gap-12 mt-8">
                <div>
                    <div className="aspect-[16/10] bg-[#E2D4C1] overflow-hidden">
                        {photos[photo] && <img src={photos[photo]} alt={`${v.year} ${v.make} ${v.model}, photo ${photo + 1} of ${photos.length}`} fetchPriority="high" className="w-full h-full object-cover" />}
                    </div>
                    {photos.length > 1 && (
                        <div className="mt-3 grid grid-cols-5 sm:grid-cols-6 gap-2" data-testid="car-gallery" aria-label="Vehicle photos">
                            {photos.map((u, i) => (
                                <button key={u} type="button" onClick={() => setPhoto(i)} aria-label={`Show photo ${i + 1}`} aria-pressed={i === photo}
                                    className={`aspect-[4/3] overflow-hidden border ${i === photo ? "border-gold" : "border-[#E2D4C1] hover:border-gold/50"}`}>
                                    <img src={u} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="mt-12">
                        <h2 className="font-display text-3xl mb-6">Specifications</h2>
                        <dl className="border border-[#E2D4C1]">
                            {specs.map(([k, val]) => (
                                <div key={k} className="grid grid-cols-2 px-6 py-3 border-b border-[#E2D4C1] last:border-0">
                                    <dt className="text-[#6B645C] uppercase tracking-wider text-xs">{k}</dt>
                                    <dd className="text-[#1A1A1A] text-sm">{val}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                    {(v.features || []).length > 0 && (
                        <div className="mt-12">
                            <h2 className="font-display text-3xl mb-4">Highlights</h2>
                            <ul className="grid sm:grid-cols-2 gap-2 text-[#33302C] text-sm list-disc pl-5">
                                {v.features.map(f => <li key={f}>{f}</li>)}
                            </ul>
                        </div>
                    )}
                    {v.description && (
                        <div className="mt-12">
                            <h2 className="font-display text-3xl mb-4">About this vehicle</h2>
                            <p className="text-[#33302C] leading-relaxed">{v.description}</p>
                        </div>
                    )}
                </div>

                <aside className="lg:sticky lg:top-28 self-start space-y-6">
                    <div className="border border-[#E2D4C1] bg-charcoal-800 p-8">
                        <div className="flex items-center justify-between gap-3 mb-2">
                            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6B645C]">{v.year}{v.body_type ? ` · ${v.body_type}` : ""}</div>
                            <VehicleStatus status={v.status} testId="vehicle-status" />
                        </div>
                        <h1 className="font-display text-3xl">{v.make}</h1>
                        <div className="text-[#33302C] mb-4">{v.model}{v.trim ? ` ${v.trim}` : ""}</div>
                        <div className="text-gold text-3xl font-medium mb-3">{fmt(v.price)}</div>
                        {(v.financing_available || docs.length > 0) && (
                            <div className="mb-6 space-y-1 text-xs text-[#33302C]">
                                {v.financing_available && <div data-testid="financing-available">Financing available: ask our sales team.</div>}
                                {docs.map(([label, url]) => (
                                    <div key={label}><a href={url} target="_blank" rel="noopener noreferrer" className="text-gold underline underline-offset-2 hover:text-gold-hover">{label}</a></div>
                                ))}
                            </div>
                        )}

                        {/* RESERVE THIS VEHICLE — refundable deposit hold */}
                        <div className="mb-6 pb-6 border-b border-[#E2D4C1]" data-testid="reserve-block">
                            {v.status === "reserved" ? (
                                <div data-testid="reserved-badge" className="text-center py-3 border border-gold/40 bg-gold/5 text-[11px] tracking-[0.25em] uppercase text-gold">
                                    Reserved
                                </div>
                            ) : v.status === "sold" ? (
                                <div data-testid="sold-badge" className="text-center py-3 border border-[#E2D4C1] text-[11px] tracking-[0.25em] uppercase text-[#6B645C]">
                                    Sold
                                </div>
                            ) : (
                                <>
                                    {v.status === "pending" && (
                                        <div data-testid="reservation-in-progress" className="mb-3 text-center py-2 border border-amber-500/40 bg-amber-500/5 text-[10px] tracking-[0.25em] uppercase text-amber-700">
                                            Reservation in progress
                                        </div>
                                    )}
                                    {!user && (
                                        <div className="mb-3">
                                            <Input
                                                aria-label="Reservation email"
                                                data-testid="reservation-email"
                                                type="email"
                                                autoComplete="email"
                                                value={form.email}
                                                onChange={e => setForm({ ...form, email: e.target.value })}
                                                placeholder="Email for your reservation receipt"
                                                className={inputCls}
                                            />
                                            <p data-testid="reservation-email-help" className="text-[11px] text-[#6B645C] mt-2">
                                                No account required. One active vehicle hold per guest.
                                            </p>
                                        </div>
                                    )}
                                    <Button
                                        data-testid="reserve-vehicle-btn"
                                        onClick={reserve}
                                        disabled={reserving}
                                        className="w-full bg-gold hover:bg-gold-hover text-white rounded-none uppercase tracking-[0.2em] text-xs font-semibold py-6"
                                    >
                                        {reserving ? "Redirecting…" : `Reserve This Vehicle — ${fmt(deposit)} Hold`}
                                    </Button>
                                    <p className="text-[11px] text-[#6B645C] mt-2 leading-relaxed">
                                        {v.status === "pending"
                                            ? "Another buyer is currently reserving this vehicle. You can still try — the hold is released if they don't complete checkout."
                                            : `Place a fully-refundable ${fmt(deposit)} deposit to hold this vehicle. Applied toward your purchase.`}
                                    </p>
                                </>
                            )}
                        </div>

                        <form onSubmit={submit} className="space-y-3" data-testid="auto-inquiry-form" aria-labelledby="inquiry-heading">
                            <Honeypot value={form.website} onChange={(val) => setForm({ ...form, website: val })} />
                            <div id="inquiry-heading" className="text-[11px] tracking-[0.3em] uppercase text-gold mb-2">
                                {v.status === "sold" ? "Looking for something similar?" : "Ask About This Vehicle"}
                            </div>
                            <Input aria-label="Your name" data-testid="inquiry-name" required autoComplete="name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your name" className={inputCls} />
                            <Input aria-label="Email" data-testid="inquiry-email" required type="email" autoComplete="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="Email" className={inputCls} />
                            <Input aria-label="Phone (optional)" data-testid="inquiry-phone" type="tel" autoComplete="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className={inputCls} />
                            <Textarea aria-label="Message" data-testid="inquiry-message" rows={3} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="Questions, trade-in, test drive, financing…" className={inputCls} />
                            <Button data-testid="inquiry-submit" type="submit" disabled={sending} className="w-full bg-gold hover:bg-gold-hover text-white rounded-none uppercase tracking-[0.2em] text-xs font-semibold">{sending ? "Sending…" : "Send Inquiry"}</Button>
                        </form>
                    </div>
                </aside>
            </div>
        </div>
    );
}
